// cloudflare media transformations
// https://developers.cloudflare.com/stream/transform-videos/
// 画像変換(/cdn-cgi/image/)と同一ゾーンの Transformations を共有する（Sources 許可リストも共通）。
export interface OptimizeVideoOptions {
  /** 出力の最大幅(px)。10〜2000 */
  width?: number;
  /** 出力の最大高さ(px)。10〜2000 */
  height?: number;
  fit?: 'contain' | 'scale-down' | 'cover';
  /** 音声を含めるか。既定は true（Cloudflare 既定） */
  audio?: boolean;
}

/** URL 生成に必要な runtimeConfig.public の最小形（画像と同じゾーンドメインを使う） */
interface VideoRuntimeConfigPublic {
  env: string;
  CLOUDFLARE_IMAGE_DOMAIN: string;
}

const MIN_DIMENSION = 10;
const MAX_DIMENSION = 2000;

/**
 * 解決済みの runtimeConfig を使って最適化済み動画 URL を組み立てる純粋関数。
 * inject を一切行わないため、computed など再評価がレンダリング外で走る箇所からでも安全に呼べる。
 *
 * 注意: 画像変換と異なり Media Transformations には `onerror` フォールバックが無い。
 * ソースオリジンは Cloudflare Transformations の Sources 許可リストに登録されている必要がある
 * （未登録だと `MEDIA_TRANSFORMATION_ERROR 9401` で 403）。
 *
 * @param config - runtimeConfig.public（`useOptimizedVideoUrl` が setup で解決して渡す）
 * @param originalUrl - 元動画 URL
 * @param options - 動画最適化オプション
 * @returns 最適化済み動画 URL（H.264/AAC の MP4 を生成）
 */
export const buildOptimizedVideoUrl = (config: VideoRuntimeConfigPublic, originalUrl: string, options: OptimizeVideoOptions = {}): string => {
  if (!originalUrl || originalUrl === '' || config.env === 'local' || !config.CLOUDFLARE_IMAGE_DOMAIN) {
    return originalUrl;
  }

  try {
    // Validate option parameters（範囲外は変換せず元 URL を返す）
    if (options.width !== undefined && (options.width < MIN_DIMENSION || options.width > MAX_DIMENSION)) {
      return originalUrl;
    }
    if (options.height !== undefined && (options.height < MIN_DIMENSION || options.height > MAX_DIMENSION)) {
      return originalUrl;
    }

    // mode=video 固定（H.264/AAC の最適化 MP4 を生成）。
    const allOptions = {
      mode: 'video',
      ...options,
    };

    const params = Object.entries(allOptions)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');
    // Cloudflare 変換のソースは絶対 URL である必要がある。
    // 先頭スラッシュのパス（同一オリジンの静的アセット）は domain を前置して絶対 URL 化する。
    const domain = config.CLOUDFLARE_IMAGE_DOMAIN.replace(/\/+$/, '');
    const source = originalUrl.startsWith('/') ? `${domain}${originalUrl}` : originalUrl;
    return `${domain}/cdn-cgi/media/${params}/${source}`;
  } catch {
    return originalUrl;
  }
};
